import { apiClient } from "@/services/api"
import { DUE_FILTERS } from "@/services/fileBrowser"
import { PROJECT_FORM_NAMES } from "@/store/project"

const authHeaders = { headers: { tenant: true, authorization: true } }

// !! Keep in sync with the backend com.lewara.backend.domain.notification.NotificationSeverity enum !!
export const SEVERITIES = {
  INFO: "INFO",
  WARNING: "WARNING",
  CRITICAL: "CRITICAL",
}

// Most urgent first. Drives list grouping and the "highest pending level" the bell is coloured by.
export const SEVERITY_ORDER = [SEVERITIES.CRITICAL, SEVERITIES.WARNING, SEVERITIES.INFO]

// Vuetify theme colours (see plugins/vuetify.js) and MDI icons per level.
export const SEVERITY_STYLE = {
  [SEVERITIES.CRITICAL]: { color: "error", icon: "mdi-alert-octagon" },
  [SEVERITIES.WARNING]: { color: "warning", icon: "mdi-alert" },
  [SEVERITIES.INFO]: { color: "info", icon: "mdi-information-outline" },
}

// !! Keep in sync with the backend com.lewara.backend.domain.notification.NotificationType enum !!
export const NOTIFICATION_TYPES = {
  UNBILLED_SERVICES: "UNBILLED_SERVICES",
  MISSING_CONSTRUCTION_REPORT: "MISSING_CONSTRUCTION_REPORT",
  PROJECT_LOSS_RISK: "PROJECT_LOSS_RISK",
  PROJECT_BUDGET_AT_RISK: "PROJECT_BUDGET_AT_RISK",
  PROJECT_COST_DRIFT: "PROJECT_COST_DRIFT",
  MONTAGE_BEHIND_PLAN: "MONTAGE_BEHIND_PLAN",
  TIME_ENTRY_MISSING: "TIME_ENTRY_MISSING",
  TIME_ENTRY_APPROVAL_PENDING: "TIME_ENTRY_APPROVAL_PENDING",
  TIME_ENTRY_RUNNING_STALE: "TIME_ENTRY_RUNNING_STALE",
  DOCUMENT_DUE: "DOCUMENT_DUE",
}

// `background` for the bell's poll; opening the page asks in the foreground.
export const fetchNotifications = ({ background = false } = {}) =>
  apiClient.get("/notifications", { ...authHeaders, background })

// An empty/absent key list means "everything currently visible to me" (mark all read).
export const markNotificationsRead = keys => apiClient.post("/notifications/read", { keys }, authHeaders)

export const fetchNotificationSettings = () => apiClient.get("/notifications/settings", authHeaders)

export const updateNotificationSettings = settings => apiClient.put("/notifications/settings", settings, authHeaders)

/**
 * Where a notification takes the user — the page on which the flagged condition can actually be
 * resolved, not just a generic project page.
 *
 * The report gap opens the project's construction-report tab directly: `useTabHash` derives the
 * active tab from the URL hash — including when only the hash changes on an already-open view — so
 * the deep link lands on the right tab.
 */
export const notificationRoute = notification => {
  switch (notification.type) {
    case NOTIFICATION_TYPES.UNBILLED_SERVICES:
      // The wizard preselects `?projectId=`, so the click lands on the project the notification
      // names instead of an empty picker the user has to fill in again.
      return { name: "HourlywageinvoiceWizard", query: { projectId: notification.projectId } }
    case NOTIFICATION_TYPES.MISSING_CONSTRUCTION_REPORT:
      return {
        name: "ProjectDetail",
        params: { projectId: notification.projectId },
        hash: `#${PROJECT_FORM_NAMES.CONSTRUCTION_REPORTS}`,
      }
    case NOTIFICATION_TYPES.MONTAGE_BEHIND_PLAN:
      // Straight to the Leistungskatalog: the alert is about what is still open, and that is the
      // screen where a Bauleiter can see which positions those are.
      return {
        name: "ProjectDetail",
        params: { projectId: notification.projectId },
        hash: `#${PROJECT_FORM_NAMES.SERVICE_CATALOG}`,
      }
    case NOTIFICATION_TYPES.PROJECT_LOSS_RISK:
    case NOTIFICATION_TYPES.PROJECT_BUDGET_AT_RISK:
    case NOTIFICATION_TYPES.PROJECT_COST_DRIFT:
      return { name: "ProjectCostsForOne", params: { projectId: notification.projectId } }
    // The three Zeiterfassung alerts, each to the screen where the thing it names can actually be
    // done — the employee and the period already filled in, so nobody has to re-select what the
    // notification just told them.
    case NOTIFICATION_TYPES.TIME_ENTRY_MISSING:
      return {
        name: "TimeTrackingReview",
        query: { userId: notification.params?.userId, from: notification.params?.oldestDate },
      }
    case NOTIFICATION_TYPES.TIME_ENTRY_APPROVAL_PENDING:
      // This one names a queue rather than a person — `TimeTrackingApprovalMonitor` aggregates the
      // whole workforce into one alert, so its params carry a count and the oldest waiting day but
      // no `userId`. It used to link to the bare screen, which opens on `review.no_selection`
      // ("Bitte zuerst Mitarbeiter auswählen"): an alert saying twelve days are waiting handed the
      // reviewer an empty page and asked them to re-select everybody to find them.
      //
      // `status=SUBMITTED` is exactly what the monitor counted — days handed in and not yet signed
      // off. OPEN is a day still being worked and was never part of that number, so filtering it in
      // would show more rows than the alert claimed. Arriving with a status and no employee makes
      // the review screen select them all, which is what turns the link into the queue itself.
      return {
        name: "TimeTrackingReview",
        query: { status: "SUBMITTED", from: notification.params?.oldestDate },
      }
    case NOTIFICATION_TYPES.TIME_ENTRY_RUNNING_STALE:
      // The live board, not the record: a running day has no end and cannot be corrected in a form —
      // somebody has to go and close it, and the board is where they can see it running. It is a tab
      // of Zeiten prüfen now, and the tab lives in the hash, so the link is unchanged but for the
      // route it names.
      return { name: "TimeTrackingReview", hash: "#attendance" }
    // Into the file browser, at the folder holding the documents that are running out. The report
    // types have no place of their own — they hang under the project they belong to — which is what
    // `parentId` is for; see `docs/file-browser.md`.
    //
    // Filtered, so the folder opens on the documents the alert was about rather than on all forty
    // of them with the reader left to find them again down the Gültig-bis column.
    //
    // Which state depends on the one the alert names. `DocumentDueMonitor` groups a record's due
    // files into one notification and puts the **most urgent** in the text ("am dringendsten „X“ am
    // …"), so `daysUntil` describes that file — and landing on that file's state is what makes the
    // link agree with the sentence the user just read. A record holding both can therefore show
    // fewer rows than `count`; the alternative was a combined "abgelaufen oder bald ablaufend"
    // filter value, which is exactly the overlapping entry the filter deliberately does not have.
    case NOTIFICATION_TYPES.DOCUMENT_DUE: {
      const { type, ownerId, parentId, daysUntil } = notification.params ?? {}
      if (!type || !ownerId) return null
      const path = parentId ? `PROJECT/${parentId}/${type}` : `${type}/${ownerId}`
      const due = Number(daysUntil) < 0 ? DUE_FILTERS.EXPIRED : DUE_FILTERS.EXPIRING
      return { name: "FileBrowser", query: { path, due } }
    }
    default:
      // A type this client does not know yet (older frontend, newer backend) simply is not clickable.
      return null
  }
}
